import type { FreeFormObject } from '@ctcells/contract'
import { OPENTRONS_DEFINITIONS } from '@ctcells/fixtures'

import type { DeckRackKind } from './DeckRackFigure.tsx'

/*
 * **데크 랙의 정의 출처 표** — 그림(`DeckRackFigure`)과 소모품 요약(`screens/monitoring/supplySummary.ts`)이
 * 같이 읽는다. 컴포넌트 파일에서 뺀 까닭은 fast refresh(컴포넌트만 내보내는 파일) 하나다 — 표의 근거와
 * 머리말은 `DeckRackFigure.tsx` 의 `LOAD_NAME` 자리에 그대로 있다.
 */
export const LOAD_NAME: Readonly<Record<Exclude<DeckRackKind, 'tcstock'>, string>> = {
  tips: 'opentrons_96_tiprack_1000ul',
  tubes6: 'opentrons_6_tuberack_falcon_50ml_conical',
  tubes4x6: 'opentrons_10_tuberack_falcon_4x50ml_6x15ml_conical',
  tubes24: 'opentrons_24_aluminumblock_nest_1.5ml_snapcap',
}

/**
 * TC 적재소 칸 수 — 우리 전용 랩웨어라 Opentrons 정의가 없다. 레시피 `assembler.tc_stock` 의 `wells` 가
 * 오면 그것이 먼저다(`rackCapacity`). 그림의 3 × 3 칸 표(`DeckRackFigure` `slotsOf`)가 이 수를 쓴다.
 */
export const TC_STOCK_SLOTS = 9

/**
 * **칸 맵의 «나머지 칸 전부» 열쇠** — `RackOccupancy` 에 이 열쇠가 있으면 맵에 이름으로 적히지 않은 칸이
 * 그 상태로 선다. 칸 이름을 모르고 «수»만 아는 쪽(`deckOccupancy` 의 0 · 가득 예외)이 쓴다.
 * 웰 이름(`A1` …)과 칸 차례(`'1'` …)는 이 글자를 쓰지 않는다.
 */
export const REST_CELLS = '*'

/** 번들 사본 — 레시피 정의가 없을 때 그림 · 칸 수가 기대는 곳 */
export function bundledDefinition(kind: DeckRackKind): FreeFormObject | undefined {
  return kind === 'tcstock' ? undefined : OPENTRONS_DEFINITIONS[LOAD_NAME[kind]]
}

/**
 * **번들 사본의 칸 차례** — 정의 `ordering`(열 차례: A1, B1 … H1, A2 …)을 펼친 웰 이름. 사본이 없거나 `ordering` 이 없으면 `undefined`.
 *
 * 🔴 **서버의 기본 차례와 같다**(2026-10-26 히나 · `ctcells-core` `app/services/recipe_v2/labware.py` `well_order()` — 「기본은 오픈트론
 *    정의의 `ordering`(열 순서) · 편집층 `pick_order: "row"` 면 줄 순서」). L2 `TipPlan.next()`(`staining_plan.py`)가 이 차례로
 *    `enumerate(…, start=1)` 해 `slot` 을 내고 모듈이 그 수를 `tips_by_rack[].used_slots` 에 적는다 ⇒ `slot=N ⇔ 차례[N-1]`.
 * ⚠ 레시피가 `pick_order: "row"` 를 골랐으면 차례가 다르다 — 그래서 **레시피 정의가 있으면 그쪽 `well_names` 가 먼저**이고
 *    (`supplySummary`), 이것은 도는 작업이 없을 때만 쓴다. 실서버 레시피 29건 중 `pick_order` 를 쓴 팁 랙은 0건(2026-10-26).
 */
export function bundledSlotOrder(kind: DeckRackKind): string[] | undefined {
  const ordering = bundledDefinition(kind)?.['ordering']
  if (!Array.isArray(ordering)) return undefined
  const wells = (ordering as unknown[]).flatMap((column) =>
    Array.isArray(column)
      ? (column as unknown[]).filter((w): w is string => typeof w === 'string')
      : [],
  )
  return wells.length > 0 ? wells : undefined
}

/**
 * **그 랙의 칸 수** — ① 레시피 정의의 `wells` 키 수 ② `well_names` 길이 ③ 번들 사본의 `wells` ④ TC 적재소는
 * `TC_STOCK_SLOTS`. 🔴 숫자(96 · 9)를 부르는 쪽에 박지 않는다 — 랩웨어가 바뀌면 정의가 말한다.
 */
export function rackCapacity(kind: DeckRackKind, definition: FreeFormObject | undefined): number {
  const fromRecipe = wellCount(definition)
  if (fromRecipe !== undefined) return fromRecipe
  if (kind === 'tcstock') return TC_STOCK_SLOTS
  return wellCount(bundledDefinition(kind)) ?? 0
}

function wellCount(def: FreeFormObject | undefined): number | undefined {
  if (def === undefined) return undefined
  const wells = def['wells']
  if (wells !== null && typeof wells === 'object' && !Array.isArray(wells)) {
    const n = Object.keys(wells).length
    if (n > 0) return n
  }
  const names = def['well_names']
  if (Array.isArray(names) && names.length > 0) return names.length
  return undefined
}
